/**
 * The fixed furniture: one header and one footer, the same on every page.
 *
 * **This is a master rule, not a default.** Before it, the platform pages
 * (`Welcome`, `Directory`, `ArtistPage`) had no header and no footer at all —
 * a visitor who landed on an artist page from a shared link saw a bare column
 * of text with nothing saying what this site was or how to leave the page.
 * The festival app had its own bar and the admin had a third arrangement.
 *
 * So the contract is: a screen renders its CONTENT. It does not draw chrome,
 * and it does not opt out of it.
 *
 * **What the header does NOT do is decide whose brand it carries.** On the
 * platform it is MyRave.io; on `ozora.myrave.io` it is OZORA's own logo, with
 * MyRave.io appearing only in the footer. That is the multi-tenant premise
 * rather than a style choice — `brand` is a prop for exactly that reason, and
 * step 49 is what it cost to get the festival half right the first time.
 */
import { useLayoutEffect, useRef, type ReactNode } from 'react';
import { BrandLockup, LogoMark, Row, Wrap } from './index';
import { useUiT } from './text';


/**
 * The app header.
 *
 * Sticky rather than fixed: it stays at the top of the scroll without being
 * taken out of flow, so no page has to know to pad itself by `--appbar-h`.
 * It can never be transparent — `--chrome-bg` is the ground at 85% with a
 * blur behind it, because it sits over scrolling act rows.
 */
export function AppHeader({
  brand, brandHref = '/', menu, controls, account, compact, brandExtra, centre, trailing,
}: {
  /** The lockup. Omitted on a tenant, which passes its own logo as `brand`. */
  brand?: ReactNode;
  /**
   * Where the lockup leads. `/` on the platform; the app shell passes the
   * app's address, since on a rave or an admin host `/` is that host's root.
   */
  brandHref?: string;
  menu?: ReactNode;
  /** At most two, each a 40px circular control. More than that does not fit. */
  controls?: ReactNode;
  account?: ReactNode;
  /**
   * Drop the wordmark and show the mark alone. Set below 360px, where the
   * lockup plus a countdown plus two controls stops fitting — step 51 is the
   * record of what happens when something in this bar is allowed to wrap.
   */
  compact?: boolean;
  /**
   * What sits after the platform brand, behind a divider — a festival's own
   * logo when the app is inside one.
   *
   * Appended rather than replacing it: on a festival host somebody is in
   * MyRave AND in that rave, and the bar should say both. The tenant app's
   * own bar used to replace the lockup entirely, so there was no way back to
   * the platform from inside a rave.
   */
  brandExtra?: ReactNode;
  /** The middle of the bar. A countdown, on a festival. */
  centre?: ReactNode;
  /**
   * After the account control — the very end of the bar.
   *
   * Reserved for a VIEW switch, which is what the list/table toggle is: it
   * changes how this page is drawn and belongs at the far edge, past the
   * things that belong to the person rather than to the page.
   */
  trailing?: ReactNode;
}) {
  const headerRef = useRef<HTMLElement>(null);
  useBrandFit(headerRef);
  return (
    <header
      ref={headerRef}
      data-print="hide"
      /*
       * `data-tenant` when a rave's brand is alongside the platform's. The bar
       * then holds a second logo, a countdown and two more controls, which
       * does not fit a phone — the rules in tokens.css shed them in order of
       * what somebody inside a rave can least afford to lose.
       */
      data-tenant={brandExtra ? '' : undefined}
      className="ui-header"
    >
      <Wrap full className="ui-chrome-header-pad">
        <Row gap={8} className="ui-header-bar">
          {menu}
          {/*
            * The two brands, together. On a computer this box is no box
            * (`display: contents`) — the platform's, a divider, the rave's. On
            * a phone inside a rave there is room for one, so they share a spot
            * and take turns, flipping over every few seconds (tokens.css,
            * `.ui-header-brands`; the user, 2026-10-07).
            */}
          <span className="ui-header-brands">
            <span className="hdr-platform ui-header-brand">
              {brand ?? <BrandLockup size={compact ? 26 : 28} href={brandHref} />}
              {brandExtra && (
                /* The divider is the whole point of the slot: it says these are
                   two brands, not one long one. */
                <span aria-hidden="true" className="ui-header-rule" />
              )}
            </span>
            {brandExtra}
          </span>
          {/* Two spacers, so the centre is the BAR's centre and does not drift
              with the width of whatever is on either side of it. */}
          <span className="ui-spacer" />
          {centre && <span className="hdr-centre ui-header-centre">{centre}</span>}
          <span className="ui-spacer" />
          {controls}
          {account}
          {trailing}
        </Row>
      </Wrap>
    </header>
  );
}

/**
 * The app footer.
 *
 * **At the end of the scroll, never fixed.** The festival pages already pin a
 * bottom tab bar to the viewport; a second fixed bar would cost 120px of an
 * 844px phone between them. `bottomInset` is the tab bar's height, so the
 * footer's last line clears it instead of hiding behind it.
 *
 * `status` is where the offline line goes — "Stored copy · updated 14:02".
 * It belongs here rather than in a banner over the content because it is true
 * of the whole page and answering "how old is this?" is not urgent enough to
 * cover a row with.
 */
export function AppFooter({
  links, locales, status, bottomInset = 0, tagline, copyright,
}: {
  links?: ReactNode;
  locales?: ReactNode;
  status?: ReactNode;
  bottomInset?: number;
  tagline?: string;
  /**
   * The "© MyRave.io" line. The app passes the button that opens its version
   * matrix (plan.md step 54); `ui/` imports nothing of the app's, so it is a
   * slot, and the plain line stands in for it elsewhere.
   */
  copyright?: ReactNode;
}) {
  const tr = useUiT();
  return (
    <footer
      data-print="hide"
      className="ui-footer"
      style={{ paddingBlockEnd: `calc(var(--sp-6) + var(--safe-b) + ${bottomInset}px)` }}
    >
      <Wrap className="ui-chrome-footer-pad">
        <div className="ui-footer-cols">
          <div className="ui-footer-brand">
            <BrandLockup size={24} href="/" />
            <div className="ui-footer-tagline">
              {tagline ?? tr('The rave time engine')}
            </div>
          </div>
          {/*
            * The caller supplies whole COLUMNS, not a flat link list. A
            * marketing footer groups its links and a festival footer has two
            * of them; laying that out here would mean this component knowing
            * which surface it is on.
            */}
          {links && (
            <div className="ui-footer-links">
              {links}
            </div>
          )}
          {locales && <div className="ui-footer-locales">{locales}</div>}
        </div>

        <div className="ui-footer-base">
          {copyright ?? <span dir="ltr">© {new Date().getFullYear()} MyRave.io</span>}
          {status}
        </div>
      </Wrap>
    </footer>
  );
}

/** Marks the app's own fallback mark for hosts that want a bare glyph. */
export { LogoMark };

/** The phone width where the two brands share one spot (tokens.css, `max-width: 560px`). */
const SHARED_SPOT = '(max-width: 560px)';

/**
 * On a phone inside a rave, MyRave.io's wordmark fills the room the bar has for it — from the brands' start to the first
 * control after them, at most the bar's height — never cropped (the user, 2026-11-03: "the MyRave.io logo is small
 * compared to the rave's logo; it needs to be as big as it can without cropping and fit the empty space"). Sets
 * `--brand-room` (the spot's width, for both brands) and `--brand-fit` (the wordmark's zoom) on the header.
 */
function useBrandFit(header: React.RefObject<HTMLElement>): void {
  useLayoutEffect(() => {
    const el = header.current;
    if (!el || typeof window.matchMedia !== 'function') return undefined;
    const fit = () => {
      const brands = el.querySelector<HTMLElement>('.ui-header-brands');
      const mark = el.querySelector<HTMLElement>('.hdr-platform :is(.ui-wordmark, .ui-lockup)');
      const bar = el.querySelector<HTMLElement>('.ui-header-bar');
      if (!el.hasAttribute('data-tenant') || !brands || !mark || !bar || !window.matchMedia(SHARED_SPOT).matches) {
        el.style.removeProperty('--brand-room'); el.style.removeProperty('--brand-fit');
        return;
      }
      // The first control after the spacers is where the room ends.
      // Layout boxes, never `getBoundingClientRect`: the brands flip (a rotateX), and a box read mid-turn is the
      // turned one — the wordmark came out tiny (the user, 2026-11-03: "now it's so small").
      const kids = [...bar.children] as HTMLElement[];
      const lastSpacer = kids.map((k) => k.classList.contains('ui-spacer')).lastIndexOf(true);
      const next = kids.slice(lastSpacer + 1).find((k) => k.offsetWidth > 0);
      if (!next || brands.offsetWidth === 0) return;
      const rtl = getComputedStyle(bar).direction === 'rtl';
      const room = Math.floor((rtl
        ? brands.offsetLeft + brands.offsetWidth - (next.offsetLeft + next.offsetWidth)
        : next.offsetLeft - brands.offsetLeft) - 8);
      // The wordmark's own size, unzoomed, against the room and the bar's height.
      mark.style.zoom = '1';
      const mw = mark.scrollWidth;
      const mh = mark.offsetHeight;
      mark.style.removeProperty('zoom');
      if (mw === 0 || mh === 0) return;
      // 80% of the most it could be (the user, 2026-11-03, on the full fit: "it's huge again — make it smaller 20%").
      const z = 0.8 * Math.min(Math.max(60, room) / mw, (bar.offsetHeight - 8) / mh);
      el.style.setProperty('--brand-room', `${Math.max(60, room)}px`);
      const zoom = Math.max(0.6, Math.min(2.5, Math.floor(z * 100) / 100));
      el.style.setProperty('--brand-fit', String(zoom));
      // The spot may get less than the room (the bar's other items squeeze it): then smaller still, never cut.
      const drawn = mark.offsetWidth;
      if (drawn > brands.clientWidth && drawn > 0) {
        el.style.setProperty('--brand-fit', String(Math.max(0.6, Math.floor(zoom * (brands.clientWidth / drawn) * 100) / 100)));
      }
    };
    // Once a frame at most, and again whenever the bar's content changes — the rave's logo, the live pill and the
    // controls arrive through portals after the header is drawn, which no resize reports (it was measured once, too
    // early, and the wordmark stayed at the fallback with its ".io" cut — the user, 2026-11-03).
    let frame = 0;
    const soon = () => { if (!frame) frame = requestAnimationFrame(() => { frame = 0; fit(); }); };
    fit();
    const watch = typeof ResizeObserver === 'function' ? new ResizeObserver(soon) : null;
    watch?.observe(el);
    const bar = el.querySelector('.ui-header-bar');
    if (bar) watch?.observe(bar);
    const changes = typeof MutationObserver === 'function' ? new MutationObserver(soon) : null;
    changes?.observe(el, { childList: true, subtree: true, attributes: true, attributeFilter: ['data-tenant'] });
    window.addEventListener('resize', soon);
    return () => {
      watch?.disconnect(); changes?.disconnect(); window.removeEventListener('resize', soon);
      if (frame) cancelAnimationFrame(frame);
    };
  }, [header]);
}
